/**
 * `useTaskCompare` — the data layer for B3.5 compare-two-dates (issue #765).
 * One query over the already-merged compare endpoint:
 *
 *     GET /api/teams/{teamId}/tasks/compare
 *         ?from=<ISO>&to=<ISO>            (two instants)
 *         | ?from_snapshot=&to_snapshot=  (two pinned snapshots)
 *       → CompareResult { from_at, to_at, added[], removed[], changed[], *_count }
 *
 * The "then vs now" diff is PURE replay-and-diff over `task_events`: the server
 * projects the tree at each instant and partitions the nodes by their fate
 * between them. The result is about THE PLAN's structure — never keyed on,
 * grouped by, or counted per person (§A9). `changed` records WHAT moved on a
 * node (state / assignment-set / parent), never WHO moved it.
 *
 * Two selection modes, mutually exclusive per side:
 *   - by instant   → `{ from, to }` ISO strings
 *   - by snapshot  → `{ fromSnapshot, toSnapshot }` snapshot ids
 * A side can mix (e.g. a snapshot on the left, "now" on the right) — we just
 * forward whichever params the caller set.
 *
 * Clean query/view split (§A7): this owns fetching + caching; the view
 * consumes `result` and renders the diff. `retry: false` + an exposed
 * `isError` lets the view degrade calmly if the endpoint isn't deployed.
 *
 * Isolation: imports only the generic `apiFetch` + the generated schema types.
 */

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";

export type CompareResult = components["schemas"]["CompareResult"];
export type NodeChange = components["schemas"]["NodeChange"];

/** The two sides of a comparison; each side is an instant OR a snapshot id. */
export interface CompareSelection {
  /** ISO instant for the "then" side. */
  from?: string | null;
  /** ISO instant for the "now" side. */
  to?: string | null;
  /** Snapshot id for the "then" side (its `at_timestamp` is used server-side). */
  fromSnapshot?: string | null;
  /** Snapshot id for the "now" side. */
  toSnapshot?: string | null;
}

/** Cache key for a comparison. Exported for tests. The selection fully keys it. */
export const COMPARE_KEY = (teamId: string, sel: CompareSelection) =>
  [
    "task-compare",
    teamId,
    sel.from ?? "",
    sel.to ?? "",
    sel.fromSnapshot ?? "",
    sel.toSnapshot ?? "",
  ] as const;

const STALE_TIME_MS = 60_000;

export interface UseTaskCompareResult {
  /** The diff, or null while loading / before a valid selection / on error. */
  readonly result: CompareResult | null;
  readonly isLoading: boolean;
  /** True on any fetch failure (incl. a not-yet-deployed 404). */
  readonly isError: boolean;
}

/** Build the `?from=…&to=…` (or snapshot) query string, omitting empty sides. */
function buildQuery(sel: CompareSelection): string {
  const params = new URLSearchParams();
  if (sel.from) params.set("from", sel.from);
  if (sel.to) params.set("to", sel.to);
  if (sel.fromSnapshot) params.set("from_snapshot", sel.fromSnapshot);
  if (sel.toSnapshot) params.set("to_snapshot", sel.toSnapshot);
  return params.toString();
}

/** A selection is queryable once BOTH sides are pinned (by instant or snapshot). */
function hasBothSides(sel: CompareSelection): boolean {
  const left = Boolean(sel.from) || Boolean(sel.fromSnapshot);
  const right = Boolean(sel.to) || Boolean(sel.toSnapshot);
  return left && right;
}

/**
 * Fetch the "then vs now" diff for a selection. `enabled` only when a team is
 * present AND both sides are pinned — no request fires for a half-built
 * selection. Each distinct selection is its own cache entry.
 */
export function useTaskCompare(
  teamId: string | undefined,
  sel: CompareSelection,
): UseTaskCompareResult {
  const ready = Boolean(teamId) && hasBothSides(sel);
  const query = useQuery({
    queryKey: COMPARE_KEY(teamId ?? "", sel),
    enabled: ready,
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<CompareResult>(`/api/teams/${teamId}/tasks/compare?${buildQuery(sel)}`),
  });

  return {
    result: query.data ?? null,
    isLoading: query.isLoading && ready,
    isError: query.isError,
  };
}
